import { Alert, Card, Empty, List, Space, Tag, Tooltip, Typography, theme } from 'antd'
import { WarningOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { translateError } from '@/shared/api/errors'
import { queryKeys } from '@/shared/api/queryKeys'
import { ExpandableHint } from '@/shared/ui/ExpandableHint'
import { usePermissions } from '@/shared/auth/usePermissions'
import { fetchInconsistencies } from './sessionApi'

/**
 * "Baza va tarmoq mos emas" paneli.
 *
 * <h2>⚠️ NEGA BU PANEL UMUMAN KERAK</h2>
 * CoA ishlamasa baza to'g'ri qoladi, tarmoqda esa eski holat davom
 * etadi. Bugungacha buni faqat amalni bajargan odam, faqat o'sha
 * lahzada ko'rardi. Ya'ni "bloklandi" deb yozilgan abonent to'liq
 * tezlikda ishlashda davom etardi va ertasiga bu haqda hech kim
 * bilmasdi.
 *
 * <h2>⚠️ SARIQ, QIZIL EMAS</h2>
 * Bu xato emas — vaqtinchalik nomuvofiqlik (CLAUDE.md §3.2, Qoida 4).
 * Qizil rang "tizim buzildi" degan xato xulosaga olib kelardi va
 * haqiqiy avariyalar orasida farqni yo'qotardi.
 *
 * <h2>⚠️ BO'SH HOLAT ALOHIDA YOZILADI</h2>
 * "Nomuvofiqlik yo'q" — bu ijobiy javob va u ko'rinishi kerak. Bo'sh
 * karta "ma'lumot yuklanmadi" bilan bir xil ko'rinardi.
 */
export function InconsistencyPanel() {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const { can } = usePermissions()
  const allowed = can('subscriber.read')

  const pending = useQuery({
    queryKey: queryKeys.sessions.inconsistencies,
    queryFn: fetchInconsistencies,
    enabled: allowed,
    // Fon jarayoni har daqiqada qayta uradi — undan tez-tez so'rash
    // ma'nosiz, kechroq so'rash esa hal bo'lgan qatorni uzoq ushlab
    // turardi.
  // ⚠️ Avtoyangilanish OLIB TASHLANDI (egasi qarori 2026-08-20):
  //    u faqat bosh sahifada qoladi. Qolgan ekranlarda u backendga
  //    doimiy yuk berardi, holbuki operator sahifani ochiq qoldirib
  //    boshqa ish bilan band bo'ladi. Yangilash QO'LDA — tugma bor.
  })

  /*
    ⚠️ HUQUQ YO'Q BO'LSA PANEL UMUMAN CHIZILMAYDI.

    Ilgari so'rov `enabled: false` bilan yuborilmasdi, lekin karta baribir
    chizilardi va ichida «Hammasi mos — baza va tarmoq bir xil» degan
    IJOBIY javob turardi. Ya'ni hech narsa so'ralmagan holat «hammasi
    joyida» bo'lib ko'rinardi.

    Qo'shimcha oqibat: qatordagi qolgan panellar (`NetworkHealthPanel`,
    `TopAgentsPanel`) huquqsiz holatda `null` qaytaradi — ular bilan bir
    xil ishlash joylashuvni ham to'g'ri qiladi.
  */
  if (!allowed) return null

  /*
    ⚠️ `pending.data ?? []` EMAS, `isSuccess`.

    Eski shakl so'rov YIQILGANDA ham bo'sh ro'yxat berardi va panel
    o'sha «Hammasi mos» xabarini chizardi — server javob bermaganini
    «nomuvofiqlik yo'q» deb ko'rsatish esa aynan shu panel qo'shilgan
    muammoni qaytarardi (o'zgarish qo'llanmagani ko'rinmay qolishi).
  */
  const rows = pending.isSuccess ? pending.data : []

  return (
    <Card
      size="small"
      title={
        <Space size={6}>
          {/* ⚠️ Rang QOTIRILMAYDI — ilgari shu yerda `#d48806` turardi va
              u yorug' mavzu uchun tanlangan edi; qorong'ida kontrastini
              yo'qotardi. Mavzu tokeni ikkalasida ham to'g'ri. */}
          <WarningOutlined style={{ color: rows.length > 0 ? token.colorWarning : undefined }} />
          <span>{t('inconsistency.title')}</span>
          {rows.length > 0 ? <Tag color="orange">{rows.length}</Tag> : null}
        </Space>
      }
      loading={pending.isPending}
      /* Qatordagi qolgan panellar bilan bir xil balandlik — aks holda
         flex qatorda bu karta kalta bo'lib, ostida bo'shliq qolardi */
      style={{ height: '100%' }}
    >
      {pending.isError ? (
        <Alert type="error" showIcon message={translateError(pending.error, t)} />
      ) : rows.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('inconsistency.none')}
        />
      ) : (
        <>
          {/*
            ⚠️ IZOH TO'LIQ QOLADI, lekin birinchi qarashda IKKI QATOR.

            Bu matn uch jumladan iborat va tor kartada to'rt qatorni
            egallardi — ya'ni panelning asosiy ishi (qaysi abonentda
            o'zgarish qo'llanmagan) izoh ostida qolardi. Matnni
            qisqartirish esa taqiqlangan: u SABABNI aytadi
            (QARORLAR.md §23.2, 6-qoida). Yechim — yashirish, kesish emas.
          */}
          <ExpandableHint text={t('inconsistency.hint')} style={{ marginBottom: 8 }} />
          <List
            size="small"
            dataSource={rows.slice(0, 6)}
            renderItem={(row) => (
              <List.Item>
                <Space direction="vertical" size={0} style={{ width: '100%' }}>
                  <Space size={6} wrap>
                    <Link to={`/subscribers?q=${encodeURIComponent(row.username)}`}>
                      {row.username}
                    </Link>
                    <Tag>{t(`inconsistency.operation.${row.operation}`)}</Tag>
                    {row.desiredValue ? <Tag color="blue">{row.desiredValue}</Tag> : null}
                  </Space>
                  {/*
                    ⚠️ Urinishlar soni KO'RSATILADI. "Hozir sezildi" va
                    "20 marta urinildi" operator uchun butunlay boshqa
                    vaziyat: birinchisi o'zi hal bo'lishi mumkin,
                    ikkinchisiga odam aralashuvi kerak.
                  */}
                  <Tooltip title={row.lastError ?? ''}>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {t('inconsistency.attempts', { count: row.attempts })}
                    </Typography.Text>
                  </Tooltip>
                </Space>
              </List.Item>
            )}
          />
          {rows.length > 6 ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('inconsistency.more', { count: rows.length - 6 })}
            </Typography.Text>
          ) : null}
        </>
      )}
    </Card>
  )
}
